import React, { useEffect, useMemo, useState } from 'react'
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Badge
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { AccountCircle, ExitToApp, Dashboard, Menu as MenuIcon, ShoppingCart } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LoginDialog from '../auth/LoginDialog'
import { useCart } from '../../contexts/CartContext'

const scrollToId = (id: string) => {
  const el = document.getElementById(id)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const Header: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated, logout } = useAuth()
  const { state: cartState, toggleCart } = useCart()

  const [loginOpen, setLoginOpen] = useState(false)
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null)
  const [mobileAnchor, setMobileAnchor] = useState<null | HTMLElement>(null)

  const warzoneGradient = useMemo(
    () => 'linear-gradient(45deg, #00e676 20%, #00d9ff 50%, #ff6d00 90%)',
    []
  )

  useEffect(() => {
    const sp = new URLSearchParams(location.search)
    if (sp.get('login') === '1') {
      setLoginOpen(true)
      sp.delete('login')
      const nextSearch = sp.toString()
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      )
    }
  }, [location.pathname, location.search, navigate])

  const openUserMenu = (e: React.MouseEvent<HTMLElement>) => setUserAnchor(e.currentTarget)
  const closeUserMenu = () => setUserAnchor(null)

  const openMobileMenu = (e: React.MouseEvent<HTMLElement>) => setMobileAnchor(e.currentTarget)
  const closeMobileMenu = () => setMobileAnchor(null)

  const goClientArea = () => {
    closeUserMenu()
    closeMobileMenu()
    navigate('/cliente')
  }

  const doLogout = () => {
    closeUserMenu()
    closeMobileMenu()
    logout()
    navigate('/')
  }

  const openLogin = () => {
    closeMobileMenu()
    setLoginOpen(true)
  }

  const goToHomeSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate(`/?scroll=${encodeURIComponent(id)}`)
      return
    }
    scrollToId(id)
  }

  const navItems = [
    { label: 'Pacotes', onClick: () => goToHomeSection('pacotes') },
    { label: 'Parcerias', onClick: () => goToHomeSection('parcerias') },
    { label: 'Como funciona', onClick: () => navigate('/como-funciona') },
    { label: 'Regras', onClick: () => navigate('/regras') }
  ] as const

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1.5 }}>
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', minWidth: 0 }}
          >
            <Avatar
              src="/favicon.ico"
              alt="Warzone Servers"
              variant="rounded"
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.2,
                border: '1px solid rgba(255,255,255,0.14)',
                background:
                  'linear-gradient(135deg, rgba(0,230,118,0.20), rgba(0,217,255,0.18), rgba(255,109,0,0.14))'
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  background: warzoneGradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.05,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                WARZONE SERVERS
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label="PALWORLD"
                  color="info"
                  sx={{ fontWeight: 900, letterSpacing: 0.6 }}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {navItems.map((item) => (
                <Button key={item.label} color="inherit" onClick={item.onClick}>
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                color="inherit"
                onClick={toggleCart}
                aria-label="carrinho"
                sx={{
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.03)',
                  '&:hover': { bgcolor: 'rgba(0,230,118,0.10)', borderColor: 'rgba(0,230,118,0.35)' }
                }}
              >
                <Badge badgeContent={cartState.totalItems} color="secondary">
                  <ShoppingCart />
                </Badge>
              </IconButton>
              {!isAuthenticated ? (
                <Button variant="contained" color="primary" onClick={() => setLoginOpen(true)}>
                  Entrar
                </Button>
              ) : (
                <>
                  <Button
                    color="inherit"
                    startIcon={<AccountCircle />}
                    onClick={openUserMenu}
                    sx={{ maxWidth: 240 }}
                  >
                    {user?.username}
                  </Button>
                  <Menu
                    anchorEl={userAnchor}
                    open={Boolean(userAnchor)}
                    onClose={closeUserMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                      sx: {
                        backgroundColor: 'rgba(15, 16, 28, 0.92)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.10)'
                      }
                    }}
                  >
                    <MenuItem onClick={goClientArea}>
                      <Dashboard fontSize="small" style={{ marginRight: 10 }} />
                      Área do Cliente
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={doLogout}>
                      <ExitToApp fontSize="small" style={{ marginRight: 10 }} />
                      Sair
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}

          {isMobile && (
            <>
              <IconButton color="inherit" onClick={openMobileMenu} aria-label="menu">
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileAnchor}
                open={Boolean(mobileAnchor)}
                onClose={closeMobileMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    backgroundColor: 'rgba(15, 16, 28, 0.92)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    minWidth: 220
                  }
                }}
              >
                {navItems.map((item) => (
                  <MenuItem
                    key={item.label}
                    onClick={() => {
                      item.onClick()
                      closeMobileMenu()
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={() => {
                    toggleCart()
                    closeMobileMenu()
                  }}
                >
                  Carrinho ({cartState.totalItems})
                </MenuItem>
                {!isAuthenticated ? (
                  <MenuItem onClick={openLogin}>Entrar</MenuItem>
                ) : (
                  <>
                    <MenuItem onClick={goClientArea}>Área do Cliente</MenuItem>
                    <MenuItem onClick={doLogout}>Sair</MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  )
}

export default Header
